import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { MatCard } from '@angular/material/card'
import { MatIcon } from '@angular/material/icon'
import { MatButtonModule } from '@angular/material/button'
import { MatProgressBar } from '@angular/material/progress-bar'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatDialog } from '@angular/material/dialog'
import { FormsModule } from '@angular/forms'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatInput } from '@angular/material/input'
import { NgClass } from '@angular/common'
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion'
import { MatTableModule } from '@angular/material/table'
import { SupplierPortalService } from '../../../core/services/supplier-portal/supplier-portal.service'
import { SnackbarCustomService } from '../../../shared/components/snackbar/snackbar-custom.service'
import { FornecedorOsVm, FornecedorPassageiroVm } from '../../../core/models/supplier-portal.model'
import { ErrorRequestModel } from '../../../core/models/response.model'
import { ModalAssignDriverComponent, AssignDriverResult } from '../../../features/supplier-portal/modal-assign-driver/modal-assign-driver.component'
import { ModalAssignVehicleComponent, AssignVehicleResult } from '../../../features/supplier-portal/modal-assign-vehicle/modal-assign-vehicle.component'

@Component({
  selector: 'supplier-portal-os-detail',
  standalone: true,
  imports: [
    MatCard, MatIcon, MatButtonModule, MatProgressBar, MatProgressSpinner,
    MatTooltipModule, FormsModule, MatFormField, MatLabel, MatInput, NgClass,
    MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle,
    MatTableModule,
  ],
  templateUrl: './supplier-portal-os-detail.component.html',
  styleUrl: './supplier-portal-os-detail.component.scss',
})
export class SupplierPortalOsDetailComponent implements OnInit {
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private supplierPortalService: SupplierPortalService,
    private snackbar: SnackbarCustomService,
  ) {}

  // Únicos status em que o fornecedor pode confirmar — o backend valida o mesmo e devolve
  // 400 com o estado atual fora disso; aqui é só pra já desabilitar o botão na UI.
  private readonly STATUS_AGUARDANDO_FORNECEDOR = 11
  private readonly STATUS_EM_REAJUSTE = 13

  osId!: string
  item: FornecedorOsVm | null = null
  obsFornecedor = ''
  loading = false
  notFound = false
  savingObs = false
  confirming = false
  // Preenchido pelo retorno das modais de atribuir motorista/veículo (CNH/antecedentes/seguro
  // vencendo em ≤30 dias). No GET normal da OS `avisos` vem sempre [], então some no próximo reload.
  avisosAtribuicao: string[] = []

  displayedPassengerColumns = ['nome', 'documento', 'contato', 'periodo', 'trecho', 'voo']

  ngOnInit() {
    this.osId = this.route.snapshot.paramMap.get('osId')!
    this.getOs()
  }

  getOs() {
    this.loading = true
    this.notFound = false
    this.avisosAtribuicao = []
    this.supplierPortalService.getOsById(this.osId).subscribe({
      next: (response: any) => {
        this.item = response.data
        this.obsFornecedor = response.data?.obsFornecedor ?? ''
      },
      error: () => { this.notFound = true; this.loading = false },
      complete: () => { this.loading = false },
    })
  }

  // No portal do fornecedor não usamos "Operador" — pra ele é sempre o Consultor
  // Operacional que confirma/recusa, não faz sentido expor o nível hierárquico interno.
  formatStatus(descricaoStatus: string): string {
    return descricaoStatus === 'Aguardando Confirmação Operador'
      ? 'Aguardando Confirmação do Consultor Operacional'
      : descricaoStatus
  }

  formatCurrency(value: any): string {
    if (value == null) return '-'
    const n = typeof value === 'number' ? value : parseFloat(value)
    if (isNaN(n)) return '-'
    return `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  formatTime(time: string | null | undefined): string {
    return time ? time.substring(0, 5) : '-'
  }

  displayValue(value: any): any {
    return value === null || value === undefined || value === '' ? '-' : value
  }

  get canConfirmar(): boolean {
    return !!this.item
      && (this.item.statusId === this.STATUS_AGUARDANDO_FORNECEDOR || this.item.statusId === this.STATUS_EM_REAJUSTE)
  }

  // Só quando o operador recusou — é o único recado que diz o que corrigir antes de reenviar.
  get motivoReajusteDisplay(): string | null {
    if (!this.item || this.item.statusId !== this.STATUS_EM_REAJUSTE) return null
    return this.item.motivoReajuste ?? null
  }

  get trechoOsDisplay(): string {
    return this.item?.enderecoCompleto ?? '-'
  }

  trechoPassageiroDisplay(p: FornecedorPassageiroVm): string {
    if (!p.enderecoOrigem && !p.enderecoDestino) {
      return '-'
    }
    return `${p.enderecoOrigem ?? '-'} → ${p.enderecoDestino ?? '-'}`
  }

  back() {
    this.router.navigate(['/portal-fornecedor'])
  }

  saveObservacao() {
    this.savingObs = true
    this.supplierPortalService.editObservacao(this.osId, this.obsFornecedor).subscribe({
      next: (response: any) => { this.snackbar.success(response.message) },
      error: (err: ErrorRequestModel) => { this.savingObs = false; this.snackbar.errorRequest(err) },
      complete: () => { this.savingObs = false },
    })
  }

  assignDriver() {
    const dialogRef = this.dialog.open(ModalAssignDriverComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      width: '1000px',
      maxWidth: '95vw',
      data: { osId: this.osId },
    })
    dialogRef.componentInstance.response.subscribe((result: AssignDriverResult) => {
      if (result.success) this.applyAssignResult(result.avisos)
    })
  }

  assignVehicle() {
    const dialogRef = this.dialog.open(ModalAssignVehicleComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      width: '1200px',
      maxWidth: '95vw',
      data: { osId: this.osId },
    })
    dialogRef.componentInstance.response.subscribe((result: AssignVehicleResult) => {
      if (result.success) this.applyAssignResult(result.avisos)
    })
  }

  private applyAssignResult(avisos: string[]) {
    this.getOs()
    this.avisosAtribuicao = avisos ?? []
  }

  confirmarOs() {
    this.confirming = true
    this.supplierPortalService.confirmarOs(this.osId).subscribe({
      next: (response: any) => {
        this.snackbar.success(response.message)
        this.getOs()
      },
      error: (err: ErrorRequestModel) => { this.confirming = false; this.snackbar.errorRequest(err) },
      complete: () => { this.confirming = false },
    })
  }
}
